import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { dashboardQuery } from '@/api/queries';
import { paths } from '@/app/paths';
import { useSession } from '@/app/session';
import { Avatar, Button, Icon, IconButton, Progress, type IconName } from '@/design-system';
import { cx } from '@/lib/cx';
import { useGlobalActions } from './globalActions';
import { Logo } from './Logo';
import s from './Sidebar.module.css';

type Item = { key: string; to: string; icon: IconName; label: string; badge?: string; warn?: boolean; match: (p: string) => boolean };

const under = (prefix: string) => (p: string) => p === prefix || p.startsWith(prefix + '/');

export function Sidebar() {
  const { t } = useTranslation('shell');
  const { t: tc } = useTranslation('common');
  const { me, isAdmin, logout } = useSession();
  const { pathname } = useLocation();
  const { openNewOrganization } = useGlobalActions();
  const side = useQuery(dashboardQuery()).data?.sidebar;

  const groups: { key: string; head: string; items: Item[] }[] = [
    {
      key: 'work',
      head: t('nav.work'),
      items: [
        { key: 'dash', to: paths.dashboard(), icon: 'layout-dashboard', label: t('nav.dashboard'), match: (p) => p === '/' },
        { key: 'orgs', to: paths.organizations(), icon: 'building-2', label: t('nav.organizations'), badge: side ? String(side.organizations) : undefined, match: under('/organizations') },
        {
          key: 'sites',
          to: paths.sites(),
          icon: 'globe',
          label: t('nav.sites'),
          badge: side?.sites_failing ? String(side.sites_failing) : undefined,
          warn: true,
          match: under('/sites'),
        },
      ],
    },
    {
      key: 'design',
      head: t('nav.design'),
      items: [
        { key: 'lib', to: paths.library(), icon: 'library-big', label: t('nav.library'), badge: side?.presets_published != null ? String(side.presets_published) : undefined, match: under('/library') },
      ],
    },
    {
      key: 'business',
      head: t('nav.business'),
      items: [{ key: 'plans', to: paths.plans(), icon: 'wallet-cards', label: t('nav.plans'), match: under('/plans') }],
    },
  ];
  if (isAdmin) {
    groups.push({
      key: 'system',
      head: t('nav.system'),
      items: [
        { key: 'settings', to: paths.settings(), icon: 'settings-2', label: t('nav.settings'), match: under('/settings') },
        { key: 'audit', to: paths.audit(), icon: 'scroll-text', label: t('nav.audit'), match: under('/audit') },
      ],
    });
  }

  // `sidebar.generations` is {month, used, limit}; used and limit are null until P4.
  const gen = side?.generations as { month?: string; used?: number | null; limit?: number | null } | undefined;
  const month = gen?.month ? (tc('months', { returnObjects: true }) as string[])[Number(gen.month.slice(5, 7)) - 1] : undefined;
  const used = typeof gen?.used === 'number' ? gen.used : null;
  const limit = typeof gen?.limit === 'number' ? gen.limit : null;
  const known = used !== null && limit !== null;
  const genPct = known && limit > 0 ? Math.round((used / limit) * 100) : 0;
  const roleLabel = isAdmin ? (me.mfa_enabled ? t('role.admin_mfa') : t('role.admin')) : t('role.pm');

  return (
    <aside className={s.aside}>
      <Logo />
      <Button size="cta" icon="plus" className={s.cta} onClick={openNewOrganization}>
        {t('new_org')}
      </Button>
      <nav className={s.nav} aria-label={t('nav.label')}>
        {groups.map((g) => (
          <div key={g.key} className={s.group}>
            <span className={s.groupHead}>{g.head}</span>
            {g.items.map((it) => {
              const active = it.match(pathname);
              return (
                <Link key={it.key} to={it.to} className={cx(s.item, active && s.active)} aria-current={active ? 'page' : undefined}>
                  <Icon name={it.icon} size={17} />
                  <span className={s.itemLabel}>{it.label}</span>
                  {it.badge && <span className={cx(s.badge, it.warn && s.badgeWarn)}>{it.badge}</span>}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
      <div className={s.bottom}>
        <div className={s.meter}>
          <div className={s.meterRow}>
            <span>{t('generations', { month: month ?? '' })}</span>
            <span className={s.meterValue}>{known ? `${used}/${limit}` : gen ? tc('dash') : ''}</span>
          </div>
          <Progress value={genPct} label={known ? t('generations_label', { used, limit }) : undefined} />
        </div>
        <div className={s.user}>
          <Avatar name={me.full_name} />
          <div className={s.userText}>
            <span className={s.userName}>{me.full_name}</span>
            <span className={s.userRole}>{roleLabel}</span>
          </div>
          <IconButton icon="log-out" label={t('logout')} tone="ghost" size={32} iconSize={15} onClick={logout} />
        </div>
      </div>
    </aside>
  );
}
